import { confirm } from '@/components/ui/confirmation-dialog';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { ShieldAlert } from 'lucide-react-native';
import { Image, Pressable, Text, View } from 'react-native';

interface AdminUser {
  id: string;
  name: string | null;
  email: string;
  image?: string | null;
  role?: string | null;
  banned?: boolean | null;
  username?: string | null;
}

interface AdminUserRowProps {
  user: AdminUser;
  currentUserId?: string;
  onSetRole: (userId: string, role: 'user' | 'admin') => void;
  onBan: (userId: string) => void;
  onUnban: (userId: string) => void;
  onViewProfile?: (userId: string, username?: string | null) => void;
}

export function AdminUserRow({
  user,
  currentUserId,
  onSetRole,
  onBan,
  onUnban,
  onViewProfile,
}: AdminUserRowProps) {
  const isAdmin = user.role === 'admin';
  const isBanned = !!user.banned;
  const isSelf = user.id === currentUserId;

  const handleRoleToggle = async () => {
    const confirmed = await confirm({
      title: isAdmin ? 'Remove admin?' : 'Grant admin?',
      description: isAdmin
        ? `Remove admin role from ${user.name || user.email}?`
        : `Grant admin role to ${user.name || user.email}?`,
      variant: isAdmin ? 'warning' : 'default',
      icon: ShieldAlert,
      confirmLabel: isAdmin ? 'Remove' : 'Confirm',
      confirmVariant: isAdmin ? 'destructive' : 'primary',
    });
    if (confirmed) {
      onSetRole(user.id, isAdmin ? 'user' : 'admin');
    }
  };

  const handleBanToggle = async () => {
    const confirmed = await confirm({
      title: isBanned ? 'Unban user?' : 'Ban user?',
      description: isBanned
        ? `Unban ${user.name || user.email}?`
        : `Ban ${user.name || user.email}? They will not be able to sign in.`,
      variant: isBanned ? 'default' : 'destructive',
      confirmLabel: isBanned ? 'Unban' : 'Ban',
      confirmVariant: isBanned ? 'primary' : 'destructive',
    });
    if (confirmed) {
      if (isBanned) {
        onUnban(user.id);
      } else {
        onBan(user.id);
      }
    }
  };

  return (
    <View
      className="border-b border-border"
      style={{ paddingVertical: 12, paddingHorizontal: 16 }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
        {user.image ? (
          <Image
            source={{ uri: imageUrl(user.image, 'thumbnail') }}
            className="border border-border"
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
            }}
            accessibilityLabel={`${user.name || 'User'}'s profile photo`}
          />
        ) : (
          <View
            className="bg-surface-secondary"
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text className="text-muted-foreground" style={{ fontSize: 16 }}>
              {user.name?.charAt(0)?.toUpperCase() || '?'}
            </Text>
          </View>
        )}

        <View style={{ flex: 1, marginLeft: 12 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Text
              className="text-foreground"
              style={{ fontSize: 15, fontWeight: '600' }}
              numberOfLines={1}
            >
              {user.name || 'Unnamed'}
            </Text>
            {isAdmin && (
              <View
                className="bg-primary"
                style={{
                  borderRadius: 4,
                  paddingHorizontal: 6,
                  paddingVertical: 1,
                }}
              >
                <Text className="text-xs font-bold text-background">
                  ADMIN
                </Text>
              </View>
            )}
            {isBanned && (
              <View
                className="bg-error"
                style={{
                  borderRadius: 4,
                  paddingHorizontal: 6,
                  paddingVertical: 1,
                }}
              >
                <Text className="text-xs font-bold" style={{ color: '#fff' }}>BANNED</Text>
              </View>
            )}
          </View>
          {user.username && (
            <Text className="text-sm text-muted-foreground" numberOfLines={1}>
              @{user.username}
            </Text>
          )}
          <Text className="text-xs text-muted-foreground" numberOfLines={1}>
            {user.email}
          </Text>
        </View>
      </View>

      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Pressable
          onPress={() => onViewProfile?.(user.id, user.username)}
          accessibilityRole="button"
          accessibilityLabel="View profile"
          className="bg-surface-secondary"
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            paddingVertical: 8,
            gap: 4,
          }}
        >
          <Ionicons name="person-outline" size={16} className="text-foreground" />
          <Text className="text-sm font-semibold text-foreground">
            View Profile
          </Text>
        </Pressable>

        <Pressable
          onPress={handleRoleToggle}
          disabled={isSelf}
          accessibilityRole="button"
          accessibilityLabel={isAdmin ? 'Remove admin role' : 'Set as admin'}
          className={isAdmin ? 'bg-surface-secondary' : 'bg-primary'}
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            paddingVertical: 8,
            gap: 4,
            opacity: isSelf ? 0.4 : 1,
          }}
        >
          <Ionicons
            name={isAdmin ? 'shield-outline' : 'shield-checkmark-outline'}
            size={16}
            className={isAdmin ? 'text-foreground' : 'text-background'}
          />
          <Text
            className={`text-sm font-semibold ${isAdmin ? 'text-foreground' : 'text-background'}`}
          >
            {isAdmin ? 'Remove Admin' : 'Set Admin'}
          </Text>
        </Pressable>

        <Pressable
          onPress={handleBanToggle}
          disabled={isSelf}
          accessibilityRole="button"
          accessibilityLabel={user.banned ? 'Unban user' : 'Ban user'}
          className="bg-surface-secondary"
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            paddingVertical: 8,
            gap: 4,
            opacity: isSelf ? 0.4 : 1,
          }}
        >
          <Ionicons
            name={isBanned ? 'lock-open-outline' : 'ban-outline'}
            size={16}
            className={isBanned ? 'text-foreground' : 'text-error'}
          />
          <Text
            className={`text-sm font-semibold ${isBanned ? 'text-foreground' : 'text-error'}`}
          >
            {isBanned ? 'Unban' : 'Ban'}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}
